import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  Timestamp,
  limit,
  getDocs
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTION_NAMES } from '../utils/constants';

export interface LeadGenerationStatus {
  id: string;
  userId: string;
  companyName: string;
  geography: string;
  status: 'Started' | 'In Progress' | 'Success' | 'Failed';
  createdAt: Timestamp;
  updatedAt: Timestamp;
  leadId?: string;
  error?: string;
  completedAt?: Timestamp;
}

export interface LeadGenerationAttempt extends LeadGenerationStatus {
  isInProgress: boolean;
  isFailed: boolean;
  isCompleted: boolean;
  elapsedTime?: number;
}

class LeadGenerationStatusService {
  private statusCollection = collection(db, COLLECTION_NAMES.LEAD_GENERATION_STATUS);

  /**
   * Subscribe to real-time updates for a user's lead generation attempts
   */
  subscribeToUserAttempts(
    userId: string,
    callback: (attempts: LeadGenerationAttempt[]) => void
  ): () => void {
    console.log('🔄 LeadGenerationStatusService: Setting up subscription for user:', userId);

    const q = query(
      this.statusCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    console.log('🔄 LeadGenerationStatusService: Query created:', q);

    // Test: Let's also try to manually fetch the data to see if it exists
    getDocs(q).then((snapshot) => {
      console.log('🔄 LeadGenerationStatusService: Manual fetch test - found', snapshot.docs.length, 'documents');
      snapshot.docs.forEach(doc => {
        console.log('🔄 LeadGenerationStatusService: Manual fetch - doc:', doc.id, doc.data());
      });
    }).catch(error => {
      console.error('❌ LeadGenerationStatusService: Manual fetch failed:', error);
    });

    return onSnapshot(q, (snapshot) => {
      console.log('🔄 LeadGenerationStatusService: Snapshot received with', snapshot.docs.length, 'documents');

      const attempts: LeadGenerationAttempt[] = snapshot.docs.map(doc => {
        const data = doc.data() as Omit<LeadGenerationStatus, 'id'>;
        const status: LeadGenerationStatus = {
          id: doc.id,
          ...data
        };

        // Calculate elapsed time for in-progress attempts
        let elapsedTime: number | undefined;
        if (status.status === 'In Progress' || status.status === 'Started') {
          const now = new Date();
          const createdAt = status.createdAt.toDate();
          elapsedTime = Math.floor((now.getTime() - createdAt.getTime()) / 1000);
        }

        const attempt = {
          ...status,
          isInProgress: status.status === 'Started' || status.status === 'In Progress',
          isFailed: status.status === 'Failed',
          isCompleted: status.status === 'Success',
          elapsedTime
        };

        return attempt;
      });

      console.log('🔄 LeadGenerationStatusService: Processed attempts:', attempts);
      callback(attempts);
    }, (error) => {
      console.error('❌ LeadGenerationStatusService: Error subscribing to lead generation status:', error);
      callback([]);
    });
  }

  /**
   * Subscribe to real-time updates for a specific company's lead generation status
   */
  subscribeToCompanyStatus(
    userId: string,
    companyName: string,
    geography: string,
    callback: (status: LeadGenerationStatus | null) => void
  ): () => void {
    const q = query(
      this.statusCollection,
      where('userId', '==', userId),
      where('companyName', '==', companyName),
      where('geography', '==', geography),
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    return onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        callback(null);
        return;
      }

      const doc = snapshot.docs[0];
      const data = doc.data() as Omit<LeadGenerationStatus, 'id'>;
      const status: LeadGenerationStatus = {
        id: doc.id,
        ...data
      };

      callback(status);
    }, (error) => {
      console.error('❌ LeadGenerationStatusService: Error subscribing to company status:', error);
      callback(null);
    });
  }

  /**
   * Get a specific lead generation status by ID
   */
  async getStatusById(statusId: string): Promise<LeadGenerationStatus | null> {
    try {
      const statusDoc = await getDoc(doc(this.statusCollection, statusId));
      if (statusDoc.exists()) {
        return {
          id: statusDoc.id,
          ...statusDoc.data()
        } as LeadGenerationStatus;
      }
      return null;
    } catch (error) {
      console.error('Error fetching lead generation status:', error);
      return null;
    }
  }

  /**
   * Format elapsed time for display
   */
  formatElapsedTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  /**
   * Create a new lead generation status record
   */
  async createStatusRecord(
    userId: string,
    companyName: string,
    geography: string
  ): Promise<string> {
    try {
      const now = Timestamp.now();
      const statusData = {
        userId: userId,
        companyName: companyName,
        geography: geography,
        status: 'Started' as const,
        createdAt: now,
        updatedAt: now
      };

      const docRef = await addDoc(this.statusCollection, statusData);
      console.log('Created lead generation status record:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating lead generation status record:', error);
      throw new Error('Failed to create status record');
    }
  }

  /**
   * Update a lead generation status record
   */
  async updateStatusRecord(
    statusId: string,
    updates: Partial<Omit<LeadGenerationStatus, 'id' | 'createdAt'>>
  ): Promise<void> {
    try {
      const statusDocRef = doc(this.statusCollection, statusId);
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };

      await updateDoc(statusDocRef, updateData);
      console.log('Updated lead generation status record:', statusId, updates);
    } catch (error) {
      console.error('Error updating lead generation status record:', error);
      throw new Error('Failed to update status record');
    }
  }

  /**
   * Mark a status record as failed
   */
  async markAsFailed(statusId: string, error: string): Promise<void> {
    return this.updateStatusRecord(statusId, {
      status: 'Failed',
      error: error,
      completedAt: Timestamp.now()
    });
  }

  /**
   * Get status display properties
   */
  getStatusDisplay(status: string) {
    switch (status) {
      case 'Started':
        return {
          color: '#0ea5e9',
          backgroundColor: '#e0f2fe',
          icon: '🚀',
          label: 'Starting...'
        };
      case 'In Progress':
        return {
          color: '#f59e0b',
          backgroundColor: '#fef3c7',
          icon: '⚡',
          label: 'Processing...'
        };
      case 'Success':
        return {
          color: '#10b981',
          backgroundColor: '#d1fae5',
          icon: '✅',
          label: 'Completed'
        };
      case 'Failed':
        return {
          color: '#ef4444',
          backgroundColor: '#fee2e2',
          icon: '❌',
          label: 'Failed'
        };
      default:
        return {
          color: '#6b7280',
          backgroundColor: '#f3f4f6',
          icon: '❓',
          label: 'Unknown'
        };
    }
  }
}

export const leadGenerationStatusService = new LeadGenerationStatusService();